import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Badge, Button, Card, Col, Row, Table, Tabs, Tag } from "antd";

import { AimOutlined, ApartmentOutlined, TeamOutlined } from "@ant-design/icons";

// Layouts & Hooks
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useDepartmentDetail } from "Hooks/AdminHooks/Organization/useDepartmentDetail";
import colors from "assets/theme/base/colors";

const { TabPane } = Tabs;

//  DesignationRow: Component for the progress bars in Distribution card

// const DesignationRow = ({ label, count, total, color }) => {
//   const widthPercent = total ? Math.round((count / total) * 100) : 0;

//   return (
//     <div className="mb-2">
//       <div className="d-flex justify-content-between align-items-center mb-1">
//         <span className="text-muted" style={{ fontSize: "0.85rem" }}>
//           {label}
//         </span>
//         <span className="text-muted" style={{ fontSize: "0.85rem" }}>
//           {count} employees
//         </span>
//       </div>
//       <div
//         style={{
//           width: "100%",
//           height: "6px", // thinner bar like the screenshot
//           backgroundColor: colors.success.main,
//           borderRadius: "4px",
//           overflow: "hidden",
//         }}
//       >
//         <div
//           style={{
//             width: `${widthPercent}%`,
//             height: "100%",
//             backgroundColor: color || "#10b981",
//             borderRadius: "4px",
//             transition: "width 0.5s ease",
//           }}
//         />
//       </div>
//     </div>
//   );
// };

// DesignationRow.propTypes = {
//   label: PropTypes.string.isRequired,
//   count: PropTypes.number.isRequired,
//   total: PropTypes.number.isRequired,
//   color: PropTypes.string,
// };

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorMap = {
    primary: colors.primary.main,
    success: colors.success.main,
    info: colors.info.main,
    warning: colors.warning.main,
    danger: colors.error.main,
  };
  const activeColor = colorMap[color] || colors.primary.main;

  return (
    <div className="col-xl-3 col-md-6 mb-3">
      <Card
        bordered={false}
        className="shadow-sm"
        style={{
          borderRadius: "10px",
          minHeight: "90px",
          backgroundColor: colors.white.main, // optional: match your pro card
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span className="text-muted small">{title}</span>
            <h3 className="fw-bold mb-0">{value}</h3>
          </div>
          {Icon && <Icon style={{ fontSize: "1.6rem", color: activeColor }} />}
        </div>
      </Card>
    </div>
  );
};

const JobTitleCard = ({ item }) => {
  return (
    <Card
      bordered={false}
      className="card-professional mb-3" //  use professional card class
      style={{ borderRadius: "12px" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">{item.name}</h6>
        <Tag color={item.isActive ? "success" : "default"}>
          {item.isActive ? "Active" : "Inactive"}
        </Tag>
      </div>

      <p className="text-secondary small mb-1">ID:</p>
      <p className="fw-bold">{item.publicId}</p>

      <p className="text-secondary small mb-1">Employees:</p>
      <p className="fw-bold">{item.employeeCount}</p>

      <p className="text-muted small mb-2">{item.description?.slice(0, 50)}...</p>

      <p className="text-secondary mb-1">Created On:</p>
      <small className="fw-bold">{new Date(item.createdOn).toLocaleDateString()}</small>
    </Card>
  );
};

const CareerPathCard = ({ item, onView }) => {
  const navigate = useNavigate();

  const updateCareerPath = () => {
    navigate(`/update-careerPath/${item.id}`, {
      state: {
        returnTo: "department-detail",
        departmentId: item.departmentId,
      },
    });
  };

  const detailCareerPath = () => {
    navigate(`/careerPathDetail/${item.id}`, {
      state: {
        returnTo: "department-detail",
        departmentId: item.departmentId,
      },
    });
  };

  return (
    <Card bordered={false} className="card-professional mb-3" style={{ borderRadius: "12px" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">{item.name}</h6>
        <Tag color={item.isActive ? "success" : "default"}>
          {item.isActive ? "Active" : "Inactive"}
        </Tag>
      </div>

      {/*  Description */}
      <p className="text-secondary small mb-1">Description:</p>
      <p className="mb-2">
        {item.description || <span className="text-muted">No description available</span>}
      </p>

      <p className="text-secondary small mb-1">Career Path ID:</p>
      <p className="fw-bold">{item.publicId}</p>

      <p className="text-secondary mb-1">Created On:</p>
      <small className="fw-bold">{new Date(item.createdOn).toLocaleDateString()}</small>

      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button size="small" className="btn-back" onClick={detailCareerPath}>
          View Details
        </Button>

        <Button size="small" className="btn-primary" onClick={updateCareerPath}>
          Update
        </Button>
      </div>
    </Card>
  );
};

const DepartmentDetail = () => {
  const { departmentId } = useParams();
  const navigate = useNavigate();
  const {
    department,
    loadingDept,
    loadingJobs,
    jobTitles,
    fetchJobTitles,
    jobTitlesLoaded,
    fecthEmployees,
    loadingEmployees,
    employeesLoaded,
    employees,
    columns,
    careerPaths,
    loadingCareerPaths,
    careerPathsLoaded,
    fetchCareerPaths,
  } = useDepartmentDetail(departmentId);

  const handleTabChange = (key) => {
    if (key === "2" && !careerPathsLoaded) {
      fetchCareerPaths(departmentId);
    }

    if (key === "3" && !jobTitlesLoaded) {
      fetchJobTitles(departmentId);
    }

    if (key === "4" && !employeesLoaded) {
      fecthEmployees(departmentId);
    }
  };

  const createJobTitle = () => {
    navigate(`/create-jobtitle/${departmentId}`, {
      state: { returnTo: "department-detail", departmentId: departmentId },
    });
  };

  const createCareerPath = () => {
    navigate(`/create-careerPath/${departmentId}`, {
      state: { returnTo: "department-detail", departmentId: departmentId },
    });
  };

  if (loadingDept || !department) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: 24 }}>Loading Departments...</div>
      </DashboardLayout>
    );
  }

  const stats = {
    jobTitles: department?.jobTitleCount || 0,
    employees: department?.employeeCount || 0,
    careerPaths: department.careerPathCount || 0,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div style={{ padding: "24px" }}>
        {/* Breadcrumb-style Header */}
        <p className="text-muted small mb-1">Departments / {department.name}</p>

        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h2 className="fw-bold mb-0">
              {department.name}{" "}
              <Tag color="success" className="ms-2">
                Active
              </Tag>
              <Tag color="processing">IT</Tag>
            </h2>
            <p className="text-muted small mb-0">
              Department ID: #{department.publicId} | Created: Jan 28 2026
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button type="default" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="row g-3 mb-4">
          <StatCard
            title="Job Titles"
            value={stats.jobTitles}
            icon={ApartmentOutlined}
            color="primary"
          />

          <StatCard title="Employees" value={stats.employees} icon={TeamOutlined} color="success" />

          <StatCard
            title="Career Paths"
            value={stats.careerPaths}
            icon={AimOutlined}
            color="info"
          />
        </div>

        {/* Content Tabs */}
        <Tabs defaultActiveKey="1" onChange={handleTabChange} className="custom-tabs">
          <TabPane
            tab={
              <span>
                <ApartmentOutlined /> Overview
              </span>
            }
            key="1"
          >
            <Row gutter={[16, 16]} className="mt-2">
              <Col xs={24} lg={16}>
                <Card bordered={false} className="shadow-sm" style={{ borderRadius: "12px" }}>
                  <h5 className="fw-bold mb-3">Department Information</h5>
                  <p>
                    <strong>Department Code:</strong> {department.publicId}
                  </p>
                  <p>
                    <strong>Status:</strong> <Tag color="success">Active</Tag>
                  </p>
                  <p>
                    <strong>Created On:</strong> Jan 28 2026
                  </p>
                  <p className="text-muted small">
                    {department.description ||
                      "Responsible for technology, network systems, and cybersecurity."}
                  </p>
                </Card>
              </Col>

              {/* <Col xs={24} lg={8}>
                <Card bordered={false} className="shadow-sm" style={{ borderRadius: "12px" }}>
                  <h5 className="fw-bold mb-3">Designation Distribution</h5>
                  <DesignationRow label="Junior" count={8} total={42} color={colors.success.main} />
                  <DesignationRow
                    label="Mid-level"
                    count={18}
                    total={42}
                    color={colors.primary.main}
                  />
                  <DesignationRow
                    label="Senior"
                    count={12}
                    total={42}
                    color={colors.warning.main}
                  />
                  <DesignationRow label="Lead" count={3} total={42} color={colors.error.main} />
                  <DesignationRow label="Principal" count={1} total={42} color={colors.info.main} />
                </Card>
              </Col> */}
            </Row>
          </TabPane>
          <TabPane
            tab={
              <span>
                <ApartmentOutlined /> Career Paths{" "}
                <Badge
                  count={department?.careerPathCount || 0}
                  style={{ backgroundColor: "var(--primary-main)" }}
                />
              </span>
            }
            key="2"
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="fw-bold mb-1">Department Career Paths</h5>
                <p className="text-muted small mb=0">
                  {" "}
                  Career Paths specific to {department.name} department
                </p>
              </div>
              <Button tclassName="btn-primary" onClick={createCareerPath}>
                {" "}
                Add Career Path
              </Button>
            </div>
            {loadingCareerPaths ? (
              <div>Loading Career Paths...</div>
            ) : (
              <Row gutter={[16, 16]}>
                {careerPaths.map((item) => (
                  <Col xs={24} md={8} key={item.id}>
                    <CareerPathCard
                      item={item}
                      onView={(cp) => navigate(`/career-path/${cp.id}`)}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>
          <TabPane
            tab={
              <span>
                <ApartmentOutlined /> Job Titles{" "}
                <Badge
                  count={department.jobTitles.length}
                  style={{ backgroundColor: "var(--primary-main)" }}
                />
              </span>
            }
            key="3"
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="fw-bold mb-1">Department Job Titles</h5>
                <p className="text-muted small mb=0">
                  {" "}
                  Job Titles specific to {department.name} department
                </p>
              </div>
              <Button tclassName="btn-primary" onClick={createJobTitle}>
                {" "}
                Add Job Title
              </Button>
            </div>
            {loadingJobs ? (
              <div>Loading Job Titles...</div>
            ) : (
              <Row gutter={[16, 16]}>
                {jobTitles.map((item) => (
                  <Col xs={24} md={8} key={item.id}>
                    <JobTitleCard item={item} />
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>
          <TabPane
            tab={
              <span>
                <TeamOutlined /> Employees{" "}
                <Badge
                  count={employees?.length || 0}
                  style={{ backgroundColor: "var(--primary-main)" }}
                />
              </span>
            }
            key="4"
          >
            <Card bordered={false} className="card-clean">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="fw-bold mb-1">Department Employees</h5>
                  <p className="text-muted small mb-0">Employees working in {department.name}</p>
                </div>
              </div>

              {loadingEmployees ? (
                <div style={{ padding: "20px", textAlign: "center" }}>Loading Employees...</div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={employees}
                  rowKey="id"
                  pagination={{ pageSize: 5 }}
                  className="custom-table"
                />
              )}
            </Card>
          </TabPane>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType,
  color: PropTypes.string,
};

CareerPathCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    departmentId: PropTypes.number.isRequired,
    publicId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    createdOn: PropTypes.string.isRequired,
    lastModifiedOn: PropTypes.string.isRequired,
  }).isRequired,
  onView: PropTypes.func, // optional handler
};

JobTitleCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    departmentId: PropTypes.number.isRequired,
    publicId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    employeeCount: PropTypes.number.isRequired,
    isActive: PropTypes.bool.isRequired,
    createdOn: PropTypes.string.isRequired,
  }).isRequired,
};

export default DepartmentDetail;
